// START IMPORTS
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Text,
  Grid,
  Link,
  IconButton,
  Stack,
  Box,
  styled,
} from '@ltvco/refresh-lib/theme';
import {
  DateUtil,
  useFirstRecordSearchRedirect,
} from '@ltvco/refresh-lib/utils';
import {
  NeedAssistanceSidebar,
  useSnackbar,
  closeSnackbar,
  useAccount,
} from '@ltvco/refresh-lib/v1';
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';

import { Sidenav } from '../components/sidenav/Sidenav';
import { SidenavContainer } from '../components/sidenav/SidenavContainer';
// END IMPORTS

const StyledContent = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: 'auto',
  marginTop: `-${theme.spacing(1)}`,
  backgroundColor: theme.palette.background.default,

  [theme.breakpoints.up('md')]: {
    margin: 'auto',
  },

  [theme.breakpoints.up('lg')]: {
    margin: 'auto',
    maxWidth: '1100px',
  },
}));

export function DashboardParent({
  contactUsHandler,
  handleLogout,
  openConfirmCancelModal,
}: {
  contactUsHandler: () => void;
  handleLogout: () => void;
  openConfirmCancelModal?: () => void;
}) {
  // START HOOKS_AND_QUERIES
  const { enqueueSnackbar } = useSnackbar();
  const { routingUtils } = useContext(AppConfig);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: account } = useAccount(true);
  const isDarkWebPage = location.pathname.includes('/dark-web');
  // END HOOKS_AND_QUERIES

  useFirstRecordSearchRedirect({
    account,
    routingUtils,
    navigate,
  });

  // START DERIVED_DATA_AND_UTILS
  const dateUtil = new DateUtil();
  const currentDate = new Date();
  const isBillingPage = location.pathname.includes('/dashboard/billing');
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (localStorage.getItem('pausedAccountSnackbarId')) {
      try {
        closeSnackbar(
          JSON.parse(localStorage.getItem('pausedAccountSnackbarId') || '')
        );
      } catch (e) {
        console.log('error', e);
      }
    }
    handleSiteNotifications();
    handleReactivationForPausedAccountsBanner();
  }, [account]);
  // END EFFECTS

  // START HELPER_FUNCTIONS
  useEffect(() => {
    if (localStorage.getItem('pausedAccountSnackbarId')) {
      try {
        closeSnackbar(
          JSON.parse(localStorage.getItem('pausedAccountSnackbarId') || '')
        );
      } catch (e) {
        console.log('error', e);
      }
    }
    handleSiteNotifications();
    handleReactivationForPausedAccountsBanner();
  }, [account]);

  function handleReactivationForPausedAccountsBanner() {
    if (!account) return;

    const isInPausedStatus = account?.meta?.subscription_state === 'paused';
    if (!isInPausedStatus) return;

    const today = currentDate.toISOString();
    const expirationDate = account?.account.subscription_info?.normalize_date;

    if (!expirationDate) return;

    const isAfterCurrentDate = dateUtil.isAfter(today, expirationDate);

    const expiredMessage = (
      <Text>
        <b>
          Your account expired on{' '}
          {dateUtil.parseDateFromString(
            account?.account.subscription_info?.normalize_date,
            'M/d/y'
          )}
          . Reactivate to regain access.
        </b>
      </Text>
    );

    const cancelledMessage = (
      <Text>
        <b>
          Your account with member ID {account?.account.user_info.user_code} has
          been cancelled.
        </b>{' '}
        <br /> You have until{' '}
        {dateUtil.parseDateFromString(
          account?.account.subscription_info?.normalize_date,
          'M/d/y'
        )}{' '}
        to continue running reports. Please reactivate now to maintain access.
        Visit{' '}
        <Link target="_blank" href="https://numberguru.com/rf/reactivation">
          here
        </Link>{' '}
        to reactivate.
      </Text>
    );

    const message = isAfterCurrentDate ? expiredMessage : cancelledMessage;

    const pausedAccountSnackbarId = enqueueSnackbar(
      <Stack direction="row" justifyContent="space-between">
        {message}
        {getCloseButton(() => {
          closeSnackbar(pausedAccountSnackbarId);
        })}
      </Stack>,
      {
        variant: 'info',
        persist: true,
        action: <></>,
      }
    );

    localStorage.setItem(
      'pausedAccountSnackbarId',
      JSON.stringify(pausedAccountSnackbarId)
    );
  }

  function getCloseButton(onClick: () => void) {
    return (
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={onClick}
        sx={{ marginLeft: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    );
  }

  function handleSiteNotifications() {
    if (!account) return;
    const siteNotification = account?.meta?.notification;
    if (!siteNotification) return;
    const siteNotificationSnackbarId = enqueueSnackbar(
      <Text>
        <b>{siteNotification.title}</b> <br /> {siteNotification.message}
      </Text>,
      {
        variant: 'info',
        persist: true,
        action: (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              closeSnackbar(siteNotificationSnackbarId);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );
  }
  // END HELPER_FUNCTIONS

  // START RENDER
  if (isDarkWebPage) {
    return (
      <StyledContent>
        <Sidenav onReport />
        <Grid container>
          <Grid size={12} maxWidth="100%">
            <Outlet />
          </Grid>
        </Grid>
      </StyledContent>
    );
  }

  return (
    <SidenavContainer>
      <Grid container justifyContent={'space-around'}>
        <Grid
          size={{
            xs: 12,
            md: isBillingPage ? 8 : 12,
          }}
          maxWidth={{
            xs: '100%',
            md: '100%',
          }}
        >
          <Outlet />
        </Grid>
        <Grid
          container
          size={{ xs: 12, md: 3, lg: 3 }}
          spacing={1}
          direction="column"
          marginTop={2}
          maxWidth={{ xs: '100%', md: '299px' }}
        >
          {isBillingPage && (
            <Grid
              width={'100%'}
              order={{ xs: 6, md: 1 }}
              className="needs-assistance"
            >
              <NeedAssistanceSidebar
                logout={handleLogout}
                supportImgUrl={constants.images.img24hr}
                contactUsHandler={contactUsHandler}
                openConfirmCancelModal={openConfirmCancelModal}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </SidenavContainer>
  );
  // END RENDER
}
